// src/context/UserPostContext.js
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import api from '../api';
import { POST_VIEW_COUNT_UPDATED_EVENT } from '../services/postViewTracker';

const UserPostContext = createContext();
const DEBUG = true;

export function UserPostProvider({ children }) {
  const [userPosts, setUserPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Boot: load cached
  useEffect(() => {
    try {
      const cached = localStorage.getItem('userProfilePosts');
      if (cached) {
        if (DEBUG) console.log('📦 [UserPostContext] Loaded from localStorage');

        const parsed = JSON.parse(cached);
        const normalized = Array.isArray(parsed)
          ? parsed.map((post) => ({
              ...post,
              viewCount: Math.max(0, Number(post?.viewCount) || 0),
            }))
          : [];

        setUserPosts(normalized);
        setHasLoaded(true);

      } else {
        if (DEBUG) console.log('🕳️ [UserPostContext] No cached posts found');
      }
    } catch (e) {
      if (DEBUG) console.warn('⚠️ [UserPostContext] Failed to parse cache', e);
    }
  }, []);

  const persist = useCallback((posts) => {
    try {
      localStorage.setItem('userProfilePosts', JSON.stringify(posts));
    } catch {}
  }, []);

  // ✅ Listen for deletes (so user profile feed stays in sync)
  useEffect(() => {
    const onExternalDelete = (e) => {
      const postId = e?.detail?.postId;
      if (!postId) return;

      setUserPosts((prev) => {
        const updated = prev.filter((p) => String(p._id) !== String(postId));
        persist(updated);
        if (DEBUG) console.log('🧹 [UserPostContext] Removed via event:', postId);
        return updated;
      });
    };

    window.addEventListener('post:deleted', onExternalDelete);
    return () => window.removeEventListener('post:deleted', onExternalDelete);
  }, [persist]);

  // ✅ Listen for edits/updates (replace in-place + persist)
  useEffect(() => {
    const onExternalUpdate = (e) => {
      const updatedPost = e?.detail?.post;
      if (!updatedPost?._id) return;

      setUserPosts((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;

        let found = false;
        const next = prev.map((p) => {
          if (String(p._id) === String(updatedPost._id)) {
            found = true;
            return {
              ...p,
              ...updatedPost,
              user: updatedPost.user || p.user,
            };
          }
          return p;
        });

        if (found) {
          persist(next);
          if (DEBUG) console.log('✏️ [UserPostContext] Updated via event:', updatedPost._id);
          return next;
        }

        return prev;
      });
    };

    window.addEventListener('post:updated', onExternalUpdate);
    return () => window.removeEventListener('post:updated', onExternalUpdate);
  }, [persist]);


  // Keep authoritative view counts synchronized and persisted
  useEffect(() => {
    const handleViewCountUpdate = (event) => {
      const postId = String(event?.detail?.postId || '');
      const nextViewCount = Number(event?.detail?.viewCount);

      if (!postId || !Number.isFinite(nextViewCount)) return;

      const safeViewCount = Math.max(0, nextViewCount);

      setUserPosts((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
          return prev;
        }

        let changed = false;

        const nextPosts = prev.map((post) => {
          if (!post) return post;

          let nextPost = post;
          const currentPostId = String(post._id || post.id || '');

          if (
            currentPostId === postId &&
            Number(post.viewCount || 0) !== safeViewCount
          ) {
            nextPost = {
              ...nextPost,
              viewCount: safeViewCount,
            };

            changed = true;
          }

          const original = nextPost.repostOf;

          if (original && typeof original === 'object') {
            const originalId = String(
              original._id || original.id || ''
            );

            if (
              originalId === postId &&
              Number(original.viewCount || 0) !== safeViewCount
            ) {
              nextPost = {
                ...nextPost,
                repostOf: {
                  ...original,
                  viewCount: safeViewCount,
                },
              };

              changed = true;
            }
          }

          return nextPost;
        });

        if (!changed) return prev;

        persist(nextPosts);
        return nextPosts;
      });
    };

    window.addEventListener(
      POST_VIEW_COUNT_UPDATED_EVENT,
      handleViewCountUpdate
    );

    return () => {
      window.removeEventListener(
        POST_VIEW_COUNT_UPDATED_EVENT,
        handleViewCountUpdate
      );
    };
  }, [persist]);


  const clearUserPostState = useCallback(() => {
    if (DEBUG) console.log('♻️ [UserPostContext] Clearing state');
    setUserPosts([]);
    setPage(1);
    setHasMore(true);
    setHasLoaded(false);
    try {
      localStorage.removeItem('userProfilePosts');
    } catch {}
  }, []);



  const addNewUserPost = useCallback(
    (newPost) => {
      const normalizedPost = {
        ...newPost,
        viewCount: Math.max(0, Number(newPost?.viewCount) || 0),
      };

      setUserPosts((prev) => {
        const exists = prev.some(
          (p) => String(p._id) === String(normalizedPost._id)
        );

        if (exists) {
          if (DEBUG) {
            console.log(
              '⚠️ [UserPostContext] Duplicate skipped:',
              normalizedPost._id
            );
          }

          return prev;
        }

        const updated = [normalizedPost, ...prev];
        persist(updated);
        return updated;
      });
    },
    [persist]
  );



  const loadMoreUserPosts = useCallback(
    (newPosts) => {
      setUserPosts((prev) => {
        const combined = [...prev];

        newPosts.forEach((post) => {
          const normalizedPost = {
            ...post,
            viewCount: Math.max(0, Number(post?.viewCount) || 0),
          };

          if (
            !combined.some(
              (p) => String(p._id) === String(normalizedPost._id)
            )
          ) {
            combined.push(normalizedPost);

          } else if (DEBUG) {
            console.log('⚠️ [UserPostContext] Duplicate skipped during loadMore:', post._id);
          }
        });
        persist(combined);
        return combined;
      });
      setHasMore(newPosts.length === 10);
      setPage((prev) => prev + 1);
    },
    [persist]
  );

  const addEnrichedUserPost = useCallback(
    async (newPost, viewerUsername) => {
      try {
        const res = await api.get(`/posts/interactions/${newPost._id}`);
        const { likes = [], comments = [], tips = [], saves = [] } = res.data || {};

        // Build mention map
        const mentionMap = {};
        if (Array.isArray(newPost.mentions)) {
          newPost.mentions.forEach((m) => {
            const key = `@${m.username?.toLowerCase()}`;
            mentionMap[key] = { username: m.username, type: m.type };
          });
        }

        const enriched = {
          ...newPost,
          viewCount: Math.max(0, Number(newPost.viewCount) || 0),
          likes,

          comments,

          tipCount: tips.length,
          likesCount: likes.length,
          commentsCount: comments.length,
          userHasLiked: likes.some((u) => u.username === viewerUsername),
          userHasSaved: Array.isArray(saves) ? saves.includes(viewerUsername) : false,
          timestamp: newPost.createdAt,
          type: 'post',
          mentionMap,
          user: newPost.user || {
            name: newPost.name,
            username: newPost.username,
            profilePic: newPost.profilePic,
            userType: newPost.userType,
            isMerchant: newPost.isMerchant,
            location: newPost.location,
          },
        };

        addNewUserPost(enriched);
        if (DEBUG) console.log('✅ [UserPostContext] Enriched & added:', enriched._id);
      } catch (err) {
        if (DEBUG) console.warn('⚠️ [UserPostContext] Failed to enrich:', newPost._id, err);
        addNewUserPost(newPost);
      }
    },
    [addNewUserPost]
  );

  const ctxValue = useMemo(
    () => ({
      userPosts,
      setUserPosts,
      page,
      setPage,
      hasMore,
      setHasMore,
      hasLoaded,
      setHasLoaded,
      clearUserPostState,
      addEnrichedUserPost,
      loadMoreUserPosts,
      addNewUserPost,
    }),
    [
      userPosts,
      page,
      hasMore,
      hasLoaded,
      clearUserPostState,
      addEnrichedUserPost,
      loadMoreUserPosts,
      addNewUserPost,
    ]
  );

  return <UserPostContext.Provider value={ctxValue}>{children}</UserPostContext.Provider>;
}

export function useUserPostContext() {
  return useContext(UserPostContext);
}
